import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useShipperAddresses, type ShipperAddress } from "@/hooks/useShipperAddresses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookUser, Plus, Trash2, ArrowLeft, Pencil } from "lucide-react";

interface EditForm extends Partial<ShipperAddress> {}

export default function LRDirectAddressBookPage() {
  usePageMeta({ title: "LR Direct — Address Book | LaneRunner", description: "Saved pickup and delivery locations for LR Direct dispatches." });
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addresses, loading, createAddress, updateAddress, deleteAddress } = useShipperAddresses(user?.id);

  const [editing, setEditing] = useState<EditForm | null>(null);

  const openNew = () => setEditing({ label: "", city: "", state: "", country: "US" });
  const openEdit = (a: ShipperAddress) => setEditing(a);

  const save = async () => {
    if (!editing || !editing.label?.trim()) return;
    if ((editing as ShipperAddress).id) {
      await updateAddress((editing as ShipperAddress).id, editing);
    } else {
      await createAddress(editing);
    }
    setEditing(null);
  };

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/lr-direct/dispatches")} className="shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-9 w-9 rounded-md bg-brand-teal/10 flex items-center justify-center shrink-0">
            <BookUser className="h-5 w-5 text-brand-teal" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">Address Book</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">Reuse pickup and delivery locations across dispatches.</p>
          </div>
        </div>
        <Button onClick={openNew} className="gap-1.5 bg-brand-teal hover:bg-brand-teal/90 w-full sm:w-auto h-11 sm:h-10">
          <Plus className="h-4 w-4" /> New Address
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : addresses.length === 0 ? (
        <Card className="p-10 text-center">
          <BookUser className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-foreground">No saved addresses yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add your common pickup and delivery sites to speed up dispatch building.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {addresses.map((a) => (
            <Card key={a.id} className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{a.label}</p>
                  {a.name && <p className="text-xs text-muted-foreground truncate">{a.name}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteAddress(a.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{a.address}</p>
              <p className="text-xs">{[a.city, a.state, a.postal_code].filter(Boolean).join(", ")}</p>
              {(a.default_contact_name || a.default_contact_phone) && (
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Contact: {[a.default_contact_name, a.default_contact_phone].filter(Boolean).join(" · ")}
                </p>
              )}
              {a.use_count > 0 && <p className="text-[10px] text-muted-foreground mt-1">Used {a.use_count}×</p>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{(editing as any)?.id ? "Edit address" : "New address"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 pt-1">
              <Field label="Label *">
                <Input value={editing.label ?? ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="e.g. Warehouse A — Dallas" />
              </Field>
              <Field label="Facility name">
                <Input value={editing.name ?? ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </Field>
              <Field label="Street address">
                <Input value={editing.address ?? ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Field label="City">
                  <Input value={editing.city ?? ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
                </Field>
                <Field label="State">
                  <Input value={editing.state ?? ""} onChange={(e) => setEditing({ ...editing, state: e.target.value.toUpperCase() })} maxLength={2} />
                </Field>
                <Field label="ZIP">
                  <Input value={editing.postal_code ?? ""} onChange={(e) => setEditing({ ...editing, postal_code: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field label="Default contact">
                  <Input value={editing.default_contact_name ?? ""} onChange={(e) => setEditing({ ...editing, default_contact_name: e.target.value })} />
                </Field>
                <Field label="Default phone">
                  <Input value={editing.default_contact_phone ?? ""} onChange={(e) => setEditing({ ...editing, default_contact_phone: e.target.value })} />
                </Field>
              </div>
              <Field label="Default instructions (dock hours, gate code, etc.)">
                <Textarea value={editing.default_instructions ?? ""} onChange={(e) => setEditing({ ...editing, default_instructions: e.target.value })} className="min-h-[60px]" />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-brand-teal hover:bg-brand-teal/90" disabled={!editing?.label?.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
